import { Injectable, Injector } from '@angular/core';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CTMeasurementFiltrableType } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-measurement-filtrable-item';
import { CTResultFiltrableType } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/ct-result-filtrable-item';
import { AxisLocation } from '@common-modules/shared/charts/model/axes/axis-location';
import {
  IVerticalAxis,
  VerticalAxis,
} from '@common-modules/shared/charts/model/axes/vertical-axis';
import {
  IPlotableSerie,
  PlotableSerie,
} from '@common-modules/shared/charts/model/series/plotable-serie';
import { SerieCustomization } from '@common-modules/shared/charts/model/series/serie-customization';
import { EventsControlSettings } from '@common-modules/shared/model/data-viz/events-control-settings.dto';
import { IFiltrableItemDto } from '@common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { FiltrableItemTypeEnum } from '@common-modules/shared/model/filtrable-items/types/filtrable-item-type-enum';
import { DimensionTypesEnum } from '@common-modules/shared/model/shared/dimension-types';
import { UnitTypeConversionViewDto } from '@common-modules/shared/model/uom/unit-type-conversion-view.dto';
import { BaseChartConfigurationService } from '@common-modules/wlm-charts/core/base-chart/base-chart-configuration.service';
import { EventViewCategory } from '@common-modules/wlm-charts/core/events-chart/models/event-view-category';
import { ChartSerieTypeEnum } from '@common-modules/wlm-charts/core/models/chart-serie-type.enum';
import { Observable, of } from 'rxjs';

@Injectable()
export class CTChartConfigurationService extends BaseChartConfigurationService {
  constructor(_injector: Injector, private _attributeService: CtAttributesService) {
    super(_injector);
  }

  getUomFromFiltrableItem(
    item: IFiltrableItemDto
  ): Observable<{ [id: string]: UnitTypeConversionViewDto }> {
    switch (item.filtrableType.type) {
      case FiltrableItemTypeEnum.CTResult:
        return this.getUomFromResult(item.filtrableType as CTResultFiltrableType);

      case FiltrableItemTypeEnum.CTMeasurement:
        return this.getUomFromMeasurement(item.filtrableType as CTMeasurementFiltrableType);

      default:
        return of(null);
    }
  }

  getEventCategories(): EventViewCategory[] {
    return [];
  }

  getDefaultEventsControlSettings(): EventsControlSettings {
    return new EventsControlSettings({ categories: [] });
  }

  getDefaultVerticalAxis(
    itemsUomConversions: Map<string, UnitTypeConversionViewDto>
  ): Map<number, IVerticalAxis> {
    const verticalAxis = new Map<number, IVerticalAxis>();

    const uniqueUnitDescriptions = Array.from(
      new Set(
        Array.from(itemsUomConversions.values())
          .map((item) => item.unitTypeToDescription)
          .filter((description) => description != null)
      )
    );

    Array.from(itemsUomConversions.values()).forEach((uom) => {
      const unitTypeToId = uom?.unitTypeToId ?? this._naUnitTypeId;
      const unitTypeToDescription = uom?.unitTypeToDescription ?? '';
      const key = uniqueUnitDescriptions.findIndex(
        (description) => description === unitTypeToDescription
      );

      if (!verticalAxis.has(unitTypeToId)) {
        const location = verticalAxis.size % 2 == 0 ? AxisLocation.Left : AxisLocation.Rigth;

        const axis = new VerticalAxis({
          id: verticalAxis.size,
          showUoM: true,
          dimensionTypeId: uom?.dimensionTypeId ?? DimensionTypesEnum.NA,
          unitTypeToId,
          unitTypeToDescription,
          location,
        });

        verticalAxis.set(key, axis);
      }
    });

    return verticalAxis;
  }

  getDefaultPlotableSerie(
    filtrableItems: IFiltrableItemDto[],
    itemsConversions: Map<string, UnitTypeConversionViewDto>,
    verticalAxis: Map<number, IVerticalAxis>,
    horizontalAxisId: number
  ): IPlotableSerie[] {
    const uniqueUnitDescriptions = Array.from(
      new Set(
        Array.from(itemsConversions.values())
          .map((item) => item.unitTypeToDescription)
          .filter((description) => description != null)
      )
    );

    const plotableSeries = filtrableItems.map((i, index) => {
      const unitTypeFromId =
        itemsConversions.get(i.filtrableType.key)?.unitTypeFromId ?? this._naUnitTypeId;
      const hexColor = this._chartHelperService.getColorByIndex(index);

      const unitTypeToDescription = itemsConversions.get(
        i.filtrableType.key
      )?.unitTypeToDescription;
      const key = uniqueUnitDescriptions.findIndex(
        (description) => description === unitTypeToDescription
      );
      // const unitTypeToId =
      //   itemsConversions.get(i.filtrableType.key)?.unitTypeToId ?? this._naUnitTypeId;
      const verticalAxisId = verticalAxis.get(key)?.id;

      const showInUtc = this.showInUtc(i.filtrableType);
      const showAlwaysInTooltip = this.showAlwaysInTooltip(i.filtrableType);

      const serieCustomization = new SerieCustomization({
        name: i.filtrableType.title,
        title: i.filtrableType.title,
        type: ChartSerieTypeEnum.Line,
        hexColor,
        unitTypeFromId,
        horizontalAxisId,
        verticalAxisId,
        showInUtc,
        showAlwaysInTooltip: showAlwaysInTooltip,
      });

      return new PlotableSerie(serieCustomization, i);
    });

    return plotableSeries;
  }

  private getUomFromResult(
    filtrableType: CTResultFiltrableType
  ): Observable<{ [id: string]: UnitTypeConversionViewDto }> {
    const result: { [id: string]: UnitTypeConversionViewDto } = {};

    result[filtrableType.key] = new UnitTypeConversionViewDto({
      conversionFactor: 1,
      dimensionTypeDescription: filtrableType.physType,
      dimensionTypeId: DimensionTypesEnum.NA,
      unitTypeFromId: null,
      unitTypeFromDescription: filtrableType.units,
      unitTypeToId: null,
      unitTypeToDescription: filtrableType.units,
      isDefaultFromUnit: true,
    });

    return of(result);
  }

  getUomFromMeasurement(
    filtrableType: CTMeasurementFiltrableType
  ): Observable<{ [id: string]: UnitTypeConversionViewDto }> {
    const { key } = filtrableType;

    const result: { [id: string]: UnitTypeConversionViewDto } = {};

    result[key] = new UnitTypeConversionViewDto({
      conversionFactor: 1,
      dimensionTypeDescription: filtrableType.unitType,
      dimensionTypeId: DimensionTypesEnum.NA,
      unitTypeFromId: null,
      unitTypeFromDescription: filtrableType.unitType,
      unitTypeToId: null,
      unitTypeToDescription: filtrableType.unitType,
      isDefaultFromUnit: true,
    });

    return of(result);
  }
}
